import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { apiErrorMessage, isValidSlackUserId } from "./slackId";

interface SlackIdInputProps {
  /** Accessible name of the text box, e.g. "Slack override for Lee Dev". */
  label: string;
  value: string | null;
  placeholder?: string;
  onSave: (slackId: string | null) => Promise<unknown>;
}

/** A Slack user id field with client-side validation. Empty clears. */
export function SlackIdInput({ label, value, placeholder = "U0123ABCD", onSave }: SlackIdInputProps) {
  const [draft, setDraft] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const errorId = useId();

  // Follow the saved value only while the field is untouched (its text still
  // equals what was last synced). A refetch — another row saved, window focus
  // — must not wipe a half-typed edit.
  const syncedRef = useRef(value ?? "");
  useEffect(() => {
    const next = value ?? "";
    setDraft((current) => (current === syncedRef.current ? next : current));
    syncedRef.current = next;
  }, [value]);

  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed && !isValidSlackUserId(trimmed)) {
      setError("Must be a Slack user id like U0123ABCD (or empty to clear).");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave(trimmed || null);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          aria-label={label}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          className="h-8 w-40 font-mono text-xs"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`Save ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
          disabled={saving || draft.trim() === (value ?? "")}
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
